/**
 * Promotions engine: simulates compare_at_price display for automatic/coupon
 * discounts configured through the `promotions` section. Pure client-side
 * rewrite of price markup + badge injection. The real discount is still
 * applied by Shopify checkout — this only recovers the visual "on sale" cue.
 *
 * Locale/currency strategy: instead of re-formatting a raw cents value with a
 * money-format template (which would be wrong when the customer browses in a
 * currency other than the shop's base), we read the already-rendered price
 * text from the DOM, derive the separator pattern from it, scale the numeric
 * portion, and reinject — preserving the display currency, separators, and
 * any inner markup like <span class="money">.
 */

const PRICE_ROOT_SELECTOR = '[data-promo-price-root]';
const BADGE_SLOT_SELECTOR = '[data-promo-badge-slot]';
const MEDIA_BADGE_SELECTOR = '[data-promo-media-badge-slot]';
const GALLERY_BADGE_SELECTOR = '[data-promo-gallery-badge-slot]';
const APPLIED_CLASS = 'promo-applied';
const BADGE_MARKER = 'data-promo-badge';
// Ancestor selectors that identify a product card root. When no ancestor
// matches (e.g. PDP main price), the card highlight is skipped.
const CARD_SELECTOR = [
  'product-card',
  '.product__card--list',
  '.product__card',
  '.product-grid-item',
  '.deals--product__card',
  '.resource-card',
  '.lookbook__shop--product',
  '.gallery-product-card',
  '.product-card-featured',
].join(', ');
// Within a card, find the image/thumbnail wrapper to attach the highlight to.
const IMAGE_WRAPPER_SELECTOR = [
  '.product-media',
  '.product__card-list__thumbnail',
  '.product__media_thumbnail',
  '.product-card__image',
  '.product-card__media',
  '.card-gallery',
  '.product__card-thumbnail',
  '.product-card-featured__media',
].join(', ');
const HIGHLIGHT_CLASS = 'promo-card--highlighted';

function parseEntries(root) {
  const map = new Map();
  root.querySelectorAll('.promotion-entry').forEach((el) => {
    const id = el.dataset.productId;
    if (!id) return;
    map.set(id, {
      type: el.dataset.discountType === 'fixed' ? 'fixed' : 'percentage',
      value: parseFloat(el.dataset.discountValue) || 0,
      displayPercentage: parseInt(el.dataset.displayPercentage, 10) || 0,
      overrideExistingSale: el.dataset.overrideExistingSale === 'true',
      colorBackground: el.dataset.colorBackground || '',
      colorText: el.dataset.colorText || '',
      badgeLabel: el.dataset.badgeLabel || '',
      cardBorderEnabled: el.dataset.cardBorderEnabled === 'true',
      cardBorderColor: el.dataset.cardBorderColor || '',
      cardBorderWidth: parseInt(el.dataset.cardBorderWidth, 10) || 2,
    });
  });
  return map;
}

/**
 * Parse a localized money string. Returns info about separators and value, or
 * null if no number found. Heuristics:
 *  - If multiple distinct separator chars appear, the LAST one is decimal.
 *  - If a single kind appears once with 1–2 trailing digits, treat as decimal.
 *  - Else all separators are thousands and precision is 0.
 */
function parseMoneyString(text) {
  if (!text) return null;
  const match = text.match(/\d[\d.,\s'\u00A0]*\d|\d/);
  if (!match) return null;
  const numStr = match[0];

  const sepMatches = [...numStr.matchAll(/[.,\s'\u00A0]/g)];
  if (sepMatches.length === 0) {
    const major = parseInt(numStr, 10);
    return {
      numStr,
      thousandsSep: '',
      decimalSep: '',
      precision: 0,
      major,
      minor: major,
    };
  }

  const sepCounts = {};
  for (const m of sepMatches) sepCounts[m[0]] = (sepCounts[m[0]] || 0) + 1;
  const uniqueSeps = Object.keys(sepCounts);
  const lastSep = sepMatches[sepMatches.length - 1];
  const lastSepChar = lastSep[0];
  const digitsAfterLast = numStr.length - lastSep.index - 1;

  let decimalSep = '';
  let thousandsSep = '';
  let precision = 0;

  if (uniqueSeps.length > 1) {
    decimalSep = lastSepChar;
    precision = digitsAfterLast;
    thousandsSep = uniqueSeps.find((s) => s !== decimalSep) || '';
  } else if (sepCounts[lastSepChar] === 1 && digitsAfterLast >= 1 && digitsAfterLast <= 2) {
    decimalSep = lastSepChar;
    precision = digitsAfterLast;
  } else {
    thousandsSep = lastSepChar;
  }

  let cleaned = numStr;
  if (thousandsSep) cleaned = cleaned.split(thousandsSep).join('');
  if (decimalSep && decimalSep !== '.') cleaned = cleaned.replace(decimalSep, '.');
  const major = parseFloat(cleaned);
  if (Number.isNaN(major)) return null;
  const minor = Math.round(major * Math.pow(10, precision));

  return { numStr, thousandsSep, decimalSep, precision, major, minor };
}

function formatMoneyInPattern(newMinor, info) {
  const newMajor = newMinor / Math.pow(10, info.precision);
  const rounded = newMajor.toFixed(info.precision);
  const [whole, frac] = rounded.split('.');
  let wholeFormatted = whole;
  if (info.thousandsSep) {
    wholeFormatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, info.thousandsSep);
  }
  if (info.precision > 0 && frac) {
    return `${wholeFormatted}${info.decimalSep || '.'}${frac}`;
  }
  return wholeFormatted;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceFirstNumberInHTML(html, oldNumStr, newNumStr) {
  // Anchor replacement with non-digit boundary on the right so we don't also
  // match a prefix of a longer number elsewhere in the markup.
  const pattern = new RegExp(`${escapeRegex(oldNumStr)}(?!\\d)`);
  return html.replace(pattern, newNumStr);
}

class PromotionsEngine {
  constructor() {
    const root = document.getElementById('promotions-data');
    if (!root) return;

    this.map = parseEntries(root);
    if (this.map.size === 0) return;

    this.badgeScheme = root.dataset.badgeScheme || 'scheme-1';
    this.saleText = root.dataset.saleText || '';
    this.saleLabel = root.dataset.saleLabel || this.saleText || 'Sale';
    this.showPercentage = root.dataset.showPercentage === 'true';

    this.applyingLocks = new WeakSet();

    this.applyAll();
    this.listen();
  }

  getPromo(productId) {
    if (!productId) return null;
    return this.map.get(String(productId)) || null;
  }

  /**
   * Compute the discount ratio (0..1) in BASE currency terms. The ratio is
   * applied to the DISPLAY-currency amount read from the DOM — so percentage
   * discounts translate cleanly, and fixed discounts are approximated by
   * their base-currency equivalent ratio.
   */
  computeSaleRatio(promo, baseMinor) {
    if (promo.type === 'percentage') {
      const pct = Math.max(0, Math.min(100, promo.value));
      return pct / 100;
    }
    const fixedBaseMinor = Math.round(promo.value * 100);
    if (!baseMinor || baseMinor <= 0) return 0;
    return Math.min(1, fixedBaseMinor / baseMinor);
  }

  percentageForBadge(promo) {
    if (promo.type === 'percentage') return Math.round(promo.value);
    return promo.displayPercentage || 0;
  }

  applyAll(scope = document) {
    scope.querySelectorAll(PRICE_ROOT_SELECTOR).forEach((el) => this.applyPrice(el));
    scope.querySelectorAll(BADGE_SLOT_SELECTOR).forEach((el) => this.applyBadge(el));
    scope.querySelectorAll(MEDIA_BADGE_SELECTOR).forEach((el) => this.applyMediaBadge(el));
    scope.querySelectorAll(GALLERY_BADGE_SELECTOR).forEach((el) => this.applyGalleryBadge(el));
  }

  applyPrice(el) {
    if (this.applyingLocks.has(el)) return;
    const promo = this.getPromo(el.dataset.productId);
    if (!promo) return;
    if (el.dataset.hasVolumePricing === 'true') return;
    // When the PDM subscription widget is active on this product, the
    // dedicated module owns pricing display (subscription + onetime cards
    // already render their own compare/sale visuals). Skipping here keeps
    // the PDP main price block from competing with Horizon's native
    // selling_plan rendering when a subscription is selected.
    if (el.closest('.pdm-active')) return;

    const baseMinor = parseInt(el.dataset.variantPrice, 10);
    if (!baseMinor || Number.isNaN(baseMinor)) return;

    const existingCompare = parseInt(el.dataset.variantComparePrice, 10) || 0;
    if (existingCompare > baseMinor && !promo.overrideExistingSale) return;

    const ratio = this.computeSaleRatio(promo, baseMinor);
    if (ratio <= 0 || ratio >= 1) return;

    const regular = el.querySelector(':scope > .price__regular');
    const sale = el.querySelector(':scope > .price__sale');
    if (!regular || !sale) return;

    const originalPriceEl = regular.querySelector('.price');
    if (!originalPriceEl) return;
    const originalHTML = originalPriceEl.innerHTML;
    const originalText = originalPriceEl.textContent || '';

    const info = parseMoneyString(originalText);
    if (!info) return;

    const saleMinor = Math.round(info.minor * (1 - ratio));
    if (saleMinor >= info.minor || saleMinor < 0) return;

    const newNumStr = formatMoneyInPattern(saleMinor, info);
    const saleHTML = replaceFirstNumberInHTML(originalHTML, info.numStr, newNumStr);

    this.applyingLocks.add(el);
    try {
      regular.classList.add('price__hidden');
      sale.classList.remove('price__hidden');
      const compareSpan = sale.querySelector('.compare-at-price');
      const saleSpan = sale.querySelector('.price-item--sale.price');
      if (compareSpan) compareSpan.innerHTML = originalHTML;
      if (saleSpan) saleSpan.innerHTML = saleHTML;

      const discountPill = sale.querySelector('[data-price-discount-pill]');
      if (discountPill) {
        const displayPct = Math.round((1 - saleMinor / info.minor) * 100);
        if (displayPct > 0) {
          discountPill.textContent = `-${displayPct}%`;
          if (promo.colorBackground) discountPill.style.background = promo.colorBackground;
          if (promo.colorText) discountPill.style.color = promo.colorText;
          discountPill.removeAttribute('hidden');
        }
      }

      el.classList.add(APPLIED_CLASS);
      el.dataset.promoApplied = 'true';

      this.applyCardBorder(el, promo);
    } finally {
      queueMicrotask(() => this.applyingLocks.delete(el));
    }
  }

  revertPrice(el) {
    // Toggle visibility back to the native state. innerHTML mutations in
    // applyPrice live inside the hidden .price__sale block, so simply
    // restoring the original visibility flags is enough.
    const regular = el.querySelector(':scope > .price__regular');
    const sale = el.querySelector(':scope > .price__sale');
    if (regular) regular.classList.remove('price__hidden');
    if (sale) sale.classList.add('price__hidden');
    el.classList.remove(APPLIED_CLASS);
    delete el.dataset.promoApplied;
  }

  applyCardBorder(priceEl, promo) {
    if (!promo.cardBorderEnabled || !promo.cardBorderColor) return;
    const card = priceEl.closest(CARD_SELECTOR);
    if (!card) return;
    // Target the image wrapper inside the card so the border frames the
    // image only and inherits the image's existing border-radius.
    const target = card.querySelector(IMAGE_WRAPPER_SELECTOR) || card;
    if (target.classList.contains(HIGHLIGHT_CLASS)) return;
    target.style.setProperty('--promo-border-color', promo.cardBorderColor);
    target.style.setProperty('--promo-border-width', `${promo.cardBorderWidth}px`);
    target.classList.add(HIGHLIGHT_CLASS);
  }

  applyBadge(el) {
    const promo = this.getPromo(el.dataset.productId);
    if (!promo) return;
    if (el.querySelector(`[${BADGE_MARKER}]`)) return;

    const hasNativeSaleBadge = el.querySelector('.sale__save--percent, .sale__text');
    if (hasNativeSaleBadge && !promo.overrideExistingSale) return;
    if (hasNativeSaleBadge && promo.overrideExistingSale) {
      el.querySelectorAll('.badge').forEach((badge) => {
        if (badge.querySelector('.sale__save--percent, .sale__text')) badge.remove();
      });
    }

    const pct = this.percentageForBadge(promo);
    const badge = document.createElement('span');
    badge.className = `badge badge--bottom-left color-${this.badgeScheme}`;
    badge.setAttribute(BADGE_MARKER, '');
    badge.setAttribute('aria-hidden', 'true');
    if (promo.colorBackground) badge.style.background = promo.colorBackground;
    if (promo.colorText) badge.style.color = promo.colorText;

    if ((this.showPercentage || promo.type === 'fixed') && pct > 0) {
      const pctSpan = document.createElement('span');
      pctSpan.className = 'sale__save--percent';
      pctSpan.textContent = `-${pct}%`;
      badge.appendChild(pctSpan);
    }
    const text = promo.badgeLabel || this.saleText;
    if (text) {
      const textSpan = document.createElement('span');
      textSpan.className = 'sale__text';
      textSpan.textContent = text;
      badge.appendChild(textSpan);
    }
    el.appendChild(badge);
  }

  applyMediaBadge(el) {
    const promo = this.getPromo(el.dataset.productId);
    if (!promo) return;
    if (el.querySelector(`[${BADGE_MARKER}]`)) return;
    if (el.querySelector('.product-badge')) return;

    const pct = this.percentageForBadge(promo);
    if (pct <= 0) return;

    const badge = document.createElement('div');
    badge.className = 'product-badge';
    badge.setAttribute(BADGE_MARKER, '');
    badge.textContent = `-${pct}%`;
    if (promo.colorBackground) badge.style.background = promo.colorBackground;
    if (promo.colorText) badge.style.color = promo.colorText;
    el.appendChild(badge);
  }

  applyGalleryBadge(el) {
    const promo = this.getPromo(el.dataset.productId);
    if (!promo) return;
    if (el.querySelector(`[${BADGE_MARKER}]`)) return;
    if (el.querySelector('.product-badges__badge')) return;

    const scheme = el.dataset.saleScheme || this.badgeScheme;
    const badge = document.createElement('div');
    badge.className = `product-badges__badge product-badges__badge--rectangle color-${scheme}`;
    badge.setAttribute(BADGE_MARKER, '');
    badge.textContent = promo.badgeLabel || this.saleLabel;
    if (promo.colorBackground) badge.style.background = promo.colorBackground;
    if (promo.colorText) badge.style.color = promo.colorText;
    el.appendChild(badge);
  }

  listen() {
    document.addEventListener('variant:update', (event) => {
      const container = event.target instanceof Element ? event.target.closest('.shopify-section, dialog') : null;
      const scope = container || document;
      requestAnimationFrame(() => this.applyAll(scope));
    });

    // PDM widget initializes async after LOOP_WIDGET. When it marks the
    // product info wrapper as pdm-active it dispatches this event so we
    // revert any price markup we may have already injected on first load.
    document.addEventListener('promotions:revert-pdp', (event) => {
      const container = event.target instanceof Element ? event.target : document;
      container.querySelectorAll(PRICE_ROOT_SELECTOR).forEach((el) => this.revertPrice(el));
    });

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(PRICE_ROOT_SELECTOR)) this.applyPrice(node);
          node.querySelectorAll?.(PRICE_ROOT_SELECTOR).forEach((el) => this.applyPrice(el));
          if (node.matches?.(BADGE_SLOT_SELECTOR)) this.applyBadge(node);
          node.querySelectorAll?.(BADGE_SLOT_SELECTOR).forEach((el) => this.applyBadge(el));
          if (node.matches?.(MEDIA_BADGE_SELECTOR)) this.applyMediaBadge(node);
          node.querySelectorAll?.(MEDIA_BADGE_SELECTOR).forEach((el) => this.applyMediaBadge(el));
          if (node.matches?.(GALLERY_BADGE_SELECTOR)) this.applyGalleryBadge(node);
          node.querySelectorAll?.(GALLERY_BADGE_SELECTOR).forEach((el) => this.applyGalleryBadge(el));
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PromotionsEngine());
} else {
  new PromotionsEngine();
}
